import type { EChartsOption, MarkLineComponentOption } from 'echarts';

import { buildAnimationConfig } from '../shared/animation';
import { FLOATING_TOOLTIP_SHADOW, buildTooltipConfig } from '../shared/tooltip';
import { buildCreditHistorySeries } from './series';
import type { CreditHistoryChartConfig, CreditHistoryPoint } from './types';

export type { CreditHistoryChartConfig, CreditHistoryPoint } from './types';

// amcharts4 DateAxis `extraMin` / `extraMax` — 1.5% of the data span breathing room at each end,
// so the first and last bullets (and their labels) clear the plot edges.
const AXIS_EXTRA = 0.015;

// Fallback extent when every point shares one date and there is no span to take `AXIS_EXTRA` of.
const LONE_POINT_PADDING = 30 * 24 * 60 * 60 * 1000;

// amcharts4 `Grid` defaults `strokeOpacity` to 0.15, and `createRange` only ever sets the grid's
// `stroke` — so every gridline in the original renders at 15%. Measured off the live AMCharts SVG.
const GRID_OPACITY = 0.15;

// amcharts4 `Cursor` lineX defaults: 40% opacity, 3px/3px dash. Also measured off the live chart —
// ECharts' 'dashed' keyword produces a longer dash than amcharts', so the pattern is explicit.
const CURSOR_OPACITY = 0.4;
const CURSOR_DASH: [number, number] = [3, 3];

// Month labels are anchored mid-month so each sits over the middle of its month, matching the
// shared line-chart convention (`shared/dateAxisLabels` labels the point nearest the 15th). A
// category axis can only land a label on a point slot, so it approximates; on a time axis we can
// use the literal 15th and get true centering.
//
// Deliberately NOT amcharts4's behavior — it opened an axis range at the *first* point of each
// month, which put "Mar 26" at Mar 1 and made a mid-month score read as if it disagreed with the
// axis. Changed under SIMPL-34172 for consistency with MultilineChart / BalancesChart.
const MONTH_LABEL_DAY = 15;

const monthTickValues = (data: CreditHistoryPoint[], min: number, max: number): number[] => {
  const seen = new Set<string>();
  const ticks: number[] = [];
  data.forEach(({ timestamp }) => {
    const date = new Date(timestamp);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    if (seen.has(key)) return;
    seen.add(key);
    const mid = new Date(date.getFullYear(), date.getMonth(), MONTH_LABEL_DAY).getTime();
    // ECharts discards out-of-extent customValues on its own, but the year rule below depends on
    // the *visible* sequence — so drop them here too, or a hidden tick could swallow "Jan 2026".
    if (mid >= min && mid <= max) ticks.push(mid);
  });
  return ticks.sort((a, b) => a - b);
};

// Shared year rule (`emitMonthLabels` in shared/dateAxisLabels): the year rides along only on
// January, or on the first visible month of a new year when January itself isn't labeled.
// So: Sep, Oct, Nov, Dec, Jan 2026, Feb, …
const buildMonthLabels = (
  ticks: number[],
  formatMonthLabel: CreditHistoryChartConfig['formatMonthLabel'],
): Map<number, string> => {
  const labels = new Map<number, string>();
  let previousYear: number | null = null;
  ticks.forEach((tick) => {
    const date = new Date(tick);
    const year = date.getFullYear();
    const showYear = previousYear === null ? date.getMonth() === 0 : year !== previousYear;
    previousYear = year;
    labels.set(tick, formatMonthLabel(tick, showYear));
  });
  return labels;
};

type TooltipItemParam = { dataIndex?: number };

export const buildCreditHistoryChartOption = (
  data: CreditHistoryPoint[],
  config: CreditHistoryChartConfig,
): EChartsOption => {
  const {
    theme,
    animated = true,
    min,
    max,
    yAxisTicks,
    axisLabelColor,
    dateAxisLabelColor,
    gridColor,
    tickColor,
    cursorLineColor,
    formatTooltip,
    formatMonthLabel,
  } = config;

  // Normalize what the types can't actually guarantee about a payload built from a service
  // response: a point with no usable date can't be placed at all, and a non-finite score or delta
  // would reach the label as a literal "NaN". A dateless point is dropped; an unusable score
  // degrades to the same null the padded month markers use, so it widens the axis but draws nothing.
  const points: CreditHistoryPoint[] = data
    .filter(({ timestamp }) => Number.isFinite(timestamp))
    .map(({ timestamp, score, delta }) => ({
      timestamp,
      score: Number.isFinite(score) ? score : null,
      delta: Number.isFinite(delta) ? delta : 0,
    }));

  // The score axis is fixed by config, never data-fitted, so it is the same whether or not there
  // is anything to plot.
  const yAxis: EChartsOption['yAxis'] = {
    type: 'value',
    // amcharts4 `strictMinMax` — the axis is the score range, never data-fitted.
    min,
    max,
    axisLabel: {
      customValues: yAxisTicks,
      color: axisLabelColor,
      fontSize: 12,
      // amcharts4 `renderer.labels.template.dx = -10`, on top of the 12px tick.
      margin: 22,
    },
    // Ticks *and* gridlines both come from axisTick.customValues: ECharts' createAxisTicks reads
    // the axisTick model's customValues whichever tick model asks for coords, so splitLine lands
    // on exactly these five band boundaries instead of auto "nice" values.
    axisTick: {
      show: true,
      customValues: yAxisTicks,
      length: 12,
      lineStyle: { color: tickColor, opacity: GRID_OPACITY },
    },
    axisLine: { show: false },
    splitLine: { show: true, lineStyle: { color: gridColor, opacity: GRID_OPACITY } },
  };

  const base: EChartsOption = {
    ...buildAnimationConfig(animated),
    textStyle: { fontFamily: theme.fontFamily },
    // amcharts4 `chart.paddingRight = 20`; the left gutter clears the y labels and their 12px ticks.
    grid: { left: 52, right: 20, top: 16, bottom: 30, containLabel: false },
    yAxis,
  };

  // No plottable points — while the card is still loading, say. Everything below derives the date
  // extent from the data, and Math.min/max of nothing is ±Infinity, which would put NaN in the
  // axis extent. Keep the score axis and leave the date axis blank rather than letting a time axis
  // with no extent invent labels around the epoch.
  if (points.length === 0) {
    return {
      ...base,
      xAxis: { type: 'time', axisLabel: { show: false }, axisTick: { show: false }, axisLine: { show: false } },
      series: [],
    };
  }

  const timestamps = points.map((point) => point.timestamp);
  const firstTimestamp = Math.min(...timestamps);
  const lastTimestamp = Math.max(...timestamps);
  const span = lastTimestamp - firstTimestamp;
  // A single point (or several on the same day) has no span to take 1.5% of, which would collapse
  // the axis to zero width — give it a month either side so the bullet sits centered instead.
  const padding = span > 0 ? span * AXIS_EXTRA : LONE_POINT_PADDING;
  const axisMin = firstTimestamp - padding;
  const axisMax = lastTimestamp + padding;

  const monthTicks = monthTickValues(points, axisMin, axisMax);
  const monthLabels = buildMonthLabels(monthTicks, formatMonthLabel);

  const series = buildCreditHistorySeries(points, config);

  // amcharts4 enables the grid on the first date range only (`disableGrid = idx !== 0`), leaving a
  // single vertical rule at the left edge of the plot. A markLine reproduces it without turning on
  // splitLine for every month tick. `silent` keeps it clear of hit-testing.
  const markLine: MarkLineComponentOption = {
    silent: true,
    symbol: 'none',
    data: [{ xAxis: firstTimestamp }],
    lineStyle: { color: gridColor, width: 1, type: 'solid', opacity: GRID_OPACITY },
    label: { show: false },
  };
  series[0] = { ...series[0], markLine };

  return {
    ...base,
    tooltip: {
      ...buildTooltipConfig(theme),
      // 'item', not 'axis': the tooltip belongs to the bullet, as it does in amcharts4. An axis
      // trigger would both anchor to the cursor instead of the point and — because ECharts drops
      // null-valued points from axis params — report the nearest scored point from anywhere in the
      // plot, so empty months would show a tooltip for a date the cursor isn't on.
      trigger: 'item',
      // amcharts4 centers the tooltip above the bullet (`pointerOrientation: 'vertical'`) rather
      // than trailing the cursor.
      position: 'top',
      // A floating card, not the bordered default box. The border radius stays at the theme's
      // amcharts4-matching value (shape.borderRadius * 2) rather than the line charts' Figma 6.
      borderWidth: 0,
      padding: 0,
      extraCssText: FLOATING_TOOLTIP_SHADOW,
      formatter: (params: unknown): string => {
        const { dataIndex } = (params ?? {}) as TooltipItemParam;
        const point = typeof dataIndex === 'number' ? points[dataIndex] : undefined;
        // Padded month markers have no score, and so no tooltip.
        if (!point || point.score === null) return '';
        return formatTooltip({ score: point.score, timestamp: point.timestamp });
      },
    },
    xAxis: {
      type: 'time',
      min: axisMin,
      max: axisMax,
      axisLabel: {
        customValues: monthTicks,
        // Precomputed rather than formatted per call: the year rule depends on the tick's place in
        // the visible sequence, which a per-value formatter cannot see.
        formatter: (value: number) => monthLabels.get(value) ?? formatMonthLabel(value, false),
        color: dateAxisLabelColor,
        fontSize: 12,
      },
      // amcharts4 date ranges: `tick.length = 6`, `tick.strokeOpacity = 0.15`.
      axisTick: {
        show: true,
        customValues: monthTicks,
        length: 6,
        lineStyle: { color: tickColor, opacity: GRID_OPACITY },
      },
      axisLine: { show: false },
      splitLine: { show: false },
      // amcharts4 XYCursor: `lineY` disabled, `lineX` stroked in border.dark, tracking the pointer
      // anywhere in the plot. Declared on the axis (not on the tooltip) so it stays independent of
      // the item-triggered tooltip; TooltipComponent installs AxisPointerComponent for us.
      // `snap: false` keeps it following the cursor smoothly instead of jumping between the sparse
      // month markers — amcharts4's day `baseInterval` reads as continuous.
      axisPointer: {
        show: true,
        snap: false,
        type: 'line',
        lineStyle: { color: cursorLineColor, width: 1, type: CURSOR_DASH, opacity: CURSOR_OPACITY },
        label: { show: false },
      },
    },
    series,
  };
};
